// src/pages/ThankYouPage.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";

function ThankYouPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const [order, setOrder] = useState(null);

  useEffect(() => {
    const data = sessionStorage.getItem("esill-order");
    if (data) setOrder(JSON.parse(data));
  }, []);

  useEffect(() => {
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "enter_thank_you",
        entity_type: "order",
        entity_id: order?.order_id || null,
      }),
    }).catch(() => {});
  }, [order?.order_id]);

  useEffect(() => {
    if (!order?.order_id) return;

    const eventId = `purchase_${order.order_id}`;

    if (typeof window.fbq === "function") {
      window.fbq(
  "track",
  "Purchase",
  {
    content_ids: order.items?.map(
      (item) => item.public_id || item.product_id
    ) || [],
    content_type: "product",
    value: Number(order.total) || 0,
    currency: "USD",
    num_items: Number(order.itemCount) || 0,
  },
  { eventID: eventId }
);
    }

    apiRequest("/analytics/purchase", {
      method: "POST",
      body: JSON.stringify({
        event_id: eventId,
        order_id: order.order_id,
        value: Number(order.total) || 0,
        email: order.email || null,
        phone: order.phone || null,
      }),
    }).catch(() => {});
  }, [order?.order_id]);

  if (!order) return null;

  const TEXT = {
    EN: {
      thanks: "Thank You!",
      summary: "Order Summary",
      products: "Products",
      delivery: "Delivery",
      total: "Total",
      loginInfo: "Your account details",
      name: "Full Name",
      phone: "Phone",
      email: "Email",
      noEmail: "No email provided",
      city: "Governorate",
      address: "Address",
      note:
        "These details will be used for your Esiell account for future orders.",
      ok: "Back to Home",
    },
    AR: {
      thanks: "شكرًا لك!",
      summary: "ملخص الطلب",
      products: "عدد المنتجات",
      delivery: "التوصيل",
      total: "الإجمالي",
      loginInfo: "بيانات حسابك",
      name: "الاسم الكامل",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني",
      noEmail: "لم يتم إدخال بريد إلكتروني",
      city: "المحافظة",
      address: "العنوان",
      note: "سيتم استخدام هذه البيانات لحسابك في Esiell.",
      ok: "العودة للرئيسية",
    },
  }[lang];

  const getDeliveryMessage = () => {
    if (order.shipping_method === "pickup") {
      return lang === "EN"
        ? "Your order will be ready for store pickup"
        : "طلبك سيكون جاهزاً للاستلام من المتجر";
    }

    const today = new Date();
    const day = today.getDay();
    const deliveryDate = new Date(today);

    if (day >= 1 && day <= 5) {
      deliveryDate.setDate(today.getDate() + 1);
    } else {
      const daysUntilTuesday = (9 - day) % 7;
      deliveryDate.setDate(today.getDate() + daysUntilTuesday);
    }

    const weekday = deliveryDate.toLocaleDateString(
      lang === "EN" ? "en-US" : "ar-LB",
      { weekday: "long" }
    );

    return lang === "EN"
      ? `Delivery expected ${weekday}`
      : `موعد التوصيل المتوقع ${weekday}`;
  };

  return (
  <div
    className="w-full min-h-screen flex items-center justify-center bg-[#0F1A12] px-4"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <div className="w-full max-w-md text-center">
      <h1 className="text-3xl font-bold text-[#E8F5E9] mb-6">
        {TEXT.thanks}
      </h1>

      {/* ORDER SUMMARY */}
      <div className="bg-[#132018] border border-[#2E5A2A]/40 rounded-xl p-5 mb-6 text-sm text-white">
        <p className="font-semibold text-lg mb-4 text-[#A5D6A7]">
          {TEXT.summary}
        </p>

        <div className="flex justify-between mb-2 opacity-90">
          <span>{TEXT.products}</span>
          <span>{order.itemCount}</span>
        </div>

        <div className="flex justify-between mb-2 opacity-90">
          <span>Subtotal</span>
          <span>${order.subtotal}</span>
        </div>

        {order.discount > 0 && (
          <div className="flex justify-between mb-2 text-[#81C784]">
            <span>Discount</span>
            <span>- ${order.discount}</span>
          </div>
        )}

        <div className="flex justify-between mb-3">
          <span>
            {order.shipping_method === "pickup"
              ? lang === "EN"
                ? "Pickup"
                : "استلام"
              : TEXT.delivery}
          </span>
          <span className="font-semibold">
            {order.shipping_method === "pickup"
              ? lang === "EN"
                ? "Free"
                : "مجاني"
              : `$${Number(order.delivery_fee || 0).toFixed(2)}`}
          </span>
        </div>

        <div className="h-px bg-[#2E5A2A]/40 my-3"></div>

        <div className="flex justify-between font-bold text-base text-[#A5D6A7]">
          <span>{TEXT.total}</span>
          <span>${order.total}</span>
        </div>

        <p className="mt-3 text-xs text-gray-400">
          {getDeliveryMessage()}
        </p>
      </div>

      {/* ACCOUNT INFO */}
      <div className="bg-[#132018] border border-[#2E5A2A]/40 rounded-xl p-5 mb-6 text-sm text-white">
        <p className="font-semibold mb-3 text-[#A5D6A7]">
          {TEXT.loginInfo}
        </p>

        <div className="flex justify-between mb-2 opacity-90">
          <span>{TEXT.name}</span>
          <span>{order.full_name}</span>
        </div>

        <div className="flex justify-between mb-2 opacity-90">
          <span>{TEXT.phone}</span>
          <span dir="ltr">{order.phone}</span>
        </div>

        <div className="flex justify-between mb-2 opacity-90">
          <span>{TEXT.email}</span>
          <span>{order.email || TEXT.noEmail}</span>
        </div>

        <div className="flex justify-between mb-2 opacity-90">
          <span>{TEXT.city}</span>
          <span>{order.city}</span>
        </div>

        <div className="flex justify-between mb-2 opacity-90">
          <span>{TEXT.address}</span>
          <span>{order.street}</span>
        </div>

        <p className="text-xs text-gray-400 mt-4">
          {TEXT.note}
        </p>
      </div>

      {/* CTA */}
      <button
        onClick={() => {
          clearCart();
          sessionStorage.removeItem("esill-order");
          navigate("/");
        }}
        className="w-full py-3 bg-[#2E5A2A] text-white rounded-lg font-semibold
                   hover:bg-[#254A22] active:scale-95 transition"
      >
        {TEXT.ok}
      </button>
    </div>
  </div>
);

}

export default ThankYouPage;
